import Phaser from "phaser";
import { GAME } from "../config";
import { preloadAllPacks, PACK_LABELS, PACKS, type PackSlug } from "../fighters/loader";
import { GamepadControls } from "../input/GamepadControls";
import { isTouchDevice } from "../input/TouchControls";
import { MultiplayerClient, getMpClient } from "../net/MultiplayerClient";
import { addFullscreenButton } from "../ui/Fullscreen";
import { DemoPreview, ensureAnimsForPack } from "../ui/DemoPreview";

// Сетка 2×2 = 4 архетипа (boxer_leo / mike / don / raph).
const COLS = 2;
const ROWS = 2;
const CELL_W = 200;
const CELL_H = 200;

// Левая зона (cells) занимает x=0..600, правая (preview) — 600..960.
const LEFT_W = 600;
const PREVIEW_X = 620;
const PREVIEW_Y = 70;
const PREVIEW_W = 320;
const PREVIEW_H = 390;

interface Slot {
  pack: PackSlug;
  slug: string;
  label: string;
}

function buildSlots(): Slot[] {
  return PACKS.slice(0, COLS * ROWS).map(pack => ({
    pack, slug: pack, label: PACK_LABELS[pack],
  }));
}

export class SelectScene extends Phaser.Scene {
  private cursors: Map<"p1" | "p2", { x: number; y: number; locked: boolean; selection?: number }> = new Map();
  private cells: Phaser.GameObjects.Container[] = [];
  private cursorGfx: Map<"p1" | "p2", Phaser.GameObjects.Rectangle> = new Map();
  private slots: Slot[] = [];

  private p1Label!: Phaser.GameObjects.Text;
  private p2Label!: Phaser.GameObjects.Text;
  private vsText!: Phaser.GameObjects.Text;
  private p1Slug?: string;
  private p2Slug?: string;
  private p1Gamepad?: GamepadControls;
  private p2Gamepad?: GamepadControls;
  private touchActivePlayer: "p1" | "p2" = "p1";
  private online = false;
  private mp?: MultiplayerClient;
  private myRole: "p1" | "p2" = "p1";

  // Two-phase handshake (online): оба должны послать start ack прежде чем перейти
  private myStartSent = false;
  private oppStartReceived = false;
  private mpUnsub?: () => void;
  private waitingText?: Phaser.GameObjects.Text;

  // Демо-превью справа
  private preview?: DemoPreview;

  constructor() { super("Select"); }

  init(data: { online?: boolean; mp?: MultiplayerClient }) {
    this.online = !!data?.online;
    this.mp = data?.mp;
    this.myRole = (this.mp?.role ?? "p1");
    this.cells = [];
    this.cursors = new Map();
    this.cursorGfx = new Map();
    this.p1Slug = undefined;
    this.p2Slug = undefined;
    this.myStartSent = false;
    this.oppStartReceived = false;
    this.slots = buildSlots();
  }

  preload() {
    preloadAllPacks(this);
  }

  create() {
    const { WIDTH, HEIGHT } = GAME;

    // Фон
    const bg = this.add.graphics();
    bg.fillGradientStyle(0x1a0808, 0x000000, 0x080000, 0x000000, 1);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    // Регистрируем анимации для всех 3 паков заранее (используются в превью)
    for (const pack of PACKS) ensureAnimsForPack(this, pack);

    // ── Заголовки ──
    this.add.text(LEFT_W / 2, 22, "ВЫБЕРИ БОЙЦА", {
      fontFamily: "Arial Black", fontSize: "26px",
      color: "#ffffff", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    this.p1Label = this.add.text(20, 22, "P1", {
      fontFamily: "Arial Black", fontSize: "22px",
      color: "#88ccff", stroke: "#000", strokeThickness: 4,
    }).setOrigin(0, 0.5);

    this.p2Label = this.add.text(LEFT_W - 20, 22, "P2", {
      fontFamily: "Arial Black", fontSize: "22px",
      color: "#ff8888", stroke: "#000", strokeThickness: 4,
    }).setOrigin(1, 0.5);

    // ── Сетка 5×3 архетипов ──
    const gridW = COLS * CELL_W;
    const gridX0 = (LEFT_W - gridW) / 2 + CELL_W / 2;
    const gridY0 = 70 + CELL_H / 2;

    this.slots.forEach((slot, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cx = gridX0 + col * CELL_W;
      const cy = gridY0 + row * CELL_H;

      const cell = this.add.container(cx, cy);

      const frame = this.add.rectangle(0, 0, CELL_W - 6, CELL_H - 6, 0x2a1f08)
        .setStrokeStyle(2, 0xffd700)
        .setInteractive({ useHandCursor: true });
      cell.add(frame);

      // Спрайт-портрет (idle frame 0) пака
      const sprite = this.add.image(0, -10, `fighter:${slot.slug}`, 0).setScale(0.85);
      cell.add(sprite);

      const nameText = this.add.text(0, CELL_H / 2 - 18, slot.label, {
        fontFamily: "Arial Black", fontSize: "20px",
        color: "#ffd700", stroke: "#000", strokeThickness: 3,
        align: "center",
      }).setOrigin(0.5);
      cell.add(nameText);

      const slotIdx = i;
      frame.on("pointerover", () => this.previewSlot(slotIdx));
      frame.on("pointerdown", () => {
        const player = this.touchActivePlayer;
        const c = this.cursors.get(player)!;
        if (c.locked) return;
        c.x = col;
        c.y = row;
        this.updateCursorPositions();
        this.previewSlot(slotIdx);
        this.confirm(player);
      });

      this.cells.push(cell);
    });

    // ── Курсоры P1/P2 ──
    const p1Cursor = this.add.rectangle(0, 0, CELL_W - 2, CELL_H - 2, 0, 0)
      .setStrokeStyle(4, 0x4499ff);
    const p2Cursor = this.add.rectangle(0, 0, CELL_W - 2, CELL_H - 2, 0, 0)
      .setStrokeStyle(4, 0xff4444);

    this.cursorGfx.set("p1", p1Cursor);
    this.cursorGfx.set("p2", p2Cursor);

    // P1 стартует на первом архетипе (col=1, row=0), P2 — на последнем студенте (col=4, row=3)
    this.cursors.set("p1", { x: 0, y: 0, locked: false });
    this.cursors.set("p2", { x: COLS - 1, y: ROWS - 1, locked: false });
    this.updateCursorPositions();

    // ── Демо-превью справа ──
    this.preview = new DemoPreview(
      this,
      PREVIEW_X + PREVIEW_W / 2,
      PREVIEW_Y + PREVIEW_H / 2,
      PREVIEW_W,
      PREVIEW_H,
    );
    // Стартуем с того, на ком стоит курсор P1
    this.previewForPlayer("p1");

    // ── Подсказка ──
    const hintLines = [
      "P1: WASD + F   |   P2: ←↑→↓ + K   |   🎮 D-Pad + A   |   ESC — назад",
    ];
    if (isTouchDevice()) {
      hintLines.push("📱 ТАП по портрету выбирает за активного игрока");
    }
    this.add.text(WIDTH / 2, HEIGHT - 28, hintLines.join("\n"), {
      fontFamily: "monospace",
      fontSize: "12px",
      color: "#888888",
      align: "center",
    }).setOrigin(0.5);

    this.vsText = this.add.text(WIDTH / 2, HEIGHT - 60, "", {
      fontFamily: "Arial Black", fontSize: "26px",
      color: "#ffd700", stroke: "#000", strokeThickness: 4,
    }).setOrigin(0.5);

    // ── Touch: переключатель P1/P2 ──
    if (isTouchDevice()) {
      const switchBtn = this.add.text(LEFT_W / 2, HEIGHT - 92, "▶ Сейчас выбирает: P1", {
        fontFamily: "Arial Black", fontSize: "14px",
        color: "#88ccff", stroke: "#000", strokeThickness: 3,
        backgroundColor: "#1a1a1a", padding: { x: 10, y: 5 },
      } as any).setOrigin(0.5).setInteractive({ useHandCursor: true });

      switchBtn.on("pointerdown", () => {
        this.touchActivePlayer = this.touchActivePlayer === "p1" ? "p2" : "p1";
        const isP1 = this.touchActivePlayer === "p1";
        switchBtn.setText(`▶ Сейчас выбирает: ${this.touchActivePlayer.toUpperCase()}`);
        switchBtn.setColor(isP1 ? "#88ccff" : "#ff8888");
        this.previewForPlayer(this.touchActivePlayer);
      });
    }

    // ── Gamepad детекция ──
    const gp = this.input.gamepad;
    if (gp) {
      const indicator = this.add.text(LEFT_W / 2, 42, "", {
        fontFamily: "monospace", fontSize: "11px", color: "#88ff88",
      }).setOrigin(0.5);

      const updateInd = () => {
        const list = gp.gamepads.filter(p => p && p.connected);
        if (list.length === 0) indicator.setText("");
        else indicator.setText(`🎮 ${list.length} геймпад(ов)`);
      };
      updateInd();
      gp.on("connected", () => { updateInd(); this.ensureGamepads(); });
      gp.on("disconnected", updateInd);
      this.ensureGamepads();
    }

    // ── Управление ──
    const KB = this.input.keyboard!;
    const wireKeys = (player: "p1" | "p2", keys: { up: string; down: string; left: string; right: string; select: string }) => {
      KB.addKey(keys.up).on("down",   () => this.move(player, 0, -1));
      KB.addKey(keys.down).on("down", () => this.move(player, 0,  1));
      KB.addKey(keys.left).on("down", () => this.move(player, -1, 0));
      KB.addKey(keys.right).on("down",() => this.move(player, 1,  0));
      KB.addKey(keys.select).on("down",() => this.confirm(player));
    };

    wireKeys("p1", { up: "W", down: "S", left: "A", right: "D", select: "F" });
    wireKeys("p2", { up: "UP", down: "DOWN", left: "LEFT", right: "RIGHT", select: "K" });

    KB.on("keydown-ESC", () => this.scene.start("MainMenu"));

    addFullscreenButton(this, GAME.WIDTH - 26, 24);

    // ── Online: получаем выбор оппонента ──
    if (this.online && this.mp) {
      const oppRole: "p1" | "p2" = this.myRole === "p1" ? "p2" : "p1";

      this.add.text(LEFT_W / 2, 42, `ONLINE — ты ${this.myRole.toUpperCase()}, оппонент ${oppRole.toUpperCase()}`, {
        fontFamily: "Arial Black", fontSize: "12px",
        color: "#88ff88", stroke: "#000", strokeThickness: 3,
      }).setOrigin(0.5);

      this.mpUnsub = this.mp.on(e => {
        if (e.type === "select" && e.from === oppRole) {
          const idx = this.findSlotIndexBySlug(e.slug);
          if (idx >= 0) {
            this.applySelection(oppRole, idx);
            this.checkBothReady();
          }
        } else if (e.type === "start" && e.from === oppRole) {
          this.oppStartReceived = true;
          this.tryStartOnline();
        } else if (e.type === "opponent_left") {
          this.add.text(GAME.WIDTH / 2, GAME.HEIGHT / 2, "СОПЕРНИК ОТКЛЮЧИЛСЯ\nESC — назад", {
            fontFamily: "Arial Black", fontSize: "32px",
            color: "#ff4444", stroke: "#000", strokeThickness: 4,
            align: "center", backgroundColor: "#000000",
            padding: { x: 30, y: 20 } as any,
          } as any).setOrigin(0.5);
        }
      });

      this.events.once("shutdown", () => {
        if (this.mpUnsub) { this.mpUnsub(); this.mpUnsub = undefined; }
      });
    }

    // Очистка превью при уничтожении сцены
    this.events.once("shutdown", () => {
      this.preview?.destroy();
      this.preview = undefined;
    });
  }

  private findSlotIndexBySlug(slug: string): number {
    return this.slots.findIndex(s => s.slug === slug);
  }

  private ensureGamepads() {
    const gp = this.input.gamepad;
    if (!gp) return;
    if (!this.p1Gamepad && gp.gamepads[0]?.connected) this.p1Gamepad = new GamepadControls(this, 0);
    if (!this.p2Gamepad && gp.gamepads[1]?.connected) this.p2Gamepad = new GamepadControls(this, 1);
  }

  update(time: number) {
    for (const player of ["p1", "p2"] as const) {
      const ctl = player === "p1" ? this.p1Gamepad : this.p2Gamepad;
      if (!ctl) continue;
      ctl.update(time);
      if (ctl.justPressed("left"))  this.move(player, -1, 0);
      if (ctl.justPressed("right")) this.move(player,  1, 0);
      if (ctl.justPressed("up"))    this.move(player,  0, -1);
      if (ctl.justPressed("down"))  this.move(player,  0,  1);
      if (ctl.justPressed("punch") || ctl.justPressed("start")) this.confirm(player);
    }
  }

  private move(player: "p1" | "p2", dx: number, dy: number) {
    const c = this.cursors.get(player)!;
    if (c.locked) return;
    c.x = Phaser.Math.Wrap(c.x + dx, 0, COLS);
    c.y = Phaser.Math.Wrap(c.y + dy, 0, ROWS);
    this.updateCursorPositions();
    this.previewForPlayer(player);
  }

  private updateCursorPositions() {
    const gridW = COLS * CELL_W;
    const gridX0 = (LEFT_W - gridW) / 2 + CELL_W / 2;
    const gridY0 = 70 + CELL_H / 2;

    for (const player of ["p1", "p2"] as const) {
      const c = this.cursors.get(player)!;
      const gfx = this.cursorGfx.get(player)!;
      gfx.setPosition(
        gridX0 + c.x * CELL_W,
        gridY0 + c.y * CELL_H,
      );
    }
  }

  /** Какого бойца показывать в превью для данного игрока (по позиции его курсора). */
  private previewForPlayer(player: "p1" | "p2") {
    const c = this.cursors.get(player);
    if (!c) return;
    const idx = c.y * COLS + c.x;
    this.previewSlot(idx);
  }

  private previewSlot(idx: number) {
    const slot = this.slots[idx];
    if (!slot) return;
    const bandanaHex = (this.mp?.bandanaColor ?? getMpClient().bandanaColor ?? "#c41e3a").replace("#", "");
    const bandanaColor = parseInt(bandanaHex, 16) || 0xc41e3a;
    this.preview?.setFighter(slot.slug, bandanaColor, slot.label);
  }

  private confirm(player: "p1" | "p2") {
    if (this.online && player !== this.myRole) return;

    const c = this.cursors.get(player)!;
    if (c.locked) return;
    const idx = c.y * COLS + c.x;
    const slot = this.slots[idx];
    if (!slot) return;

    this.applySelection(player, idx);

    if (this.online && this.mp && player === this.myRole) {
      this.mp.sendSelect(slot.slug);
    }

    this.checkBothReady();
  }

  private applySelection(player: "p1" | "p2", idx: number) {
    const c = this.cursors.get(player)!;
    if (c.locked) return;
    const slot = this.slots[idx];
    if (!slot) return;

    c.locked = true;
    c.selection = idx;

    if (player === "p1") {
      this.p1Slug = slot.slug;
      this.p1Label.setText(`P1: ${slot.label}`);
    } else {
      this.p2Slug = slot.slug;
      this.p2Label.setText(`P2: ${slot.label}`);
    }

    const cell = this.cells[idx];
    if (cell) {
      const frame = cell.list[0] as Phaser.GameObjects.Rectangle;
      frame.setStrokeStyle(4, player === "p1" ? 0x4499ff : 0xff4444);
    }
  }

  private checkBothReady() {
    if (!this.p1Slug || !this.p2Slug) return;

    this.vsText.setText(`${this.p1Slug.toUpperCase()}  VS  ${this.p2Slug.toUpperCase()}`);

    if (!this.online || !this.mp) {
      this.time.delayedCall(1200, () => this.transitionToFight());
      return;
    }

    if (!this.myStartSent) {
      this.mp.sendStart();
      this.myStartSent = true;
    }
    this.tryStartOnline();
  }

  private tryStartOnline() {
    if (!this.online) return;
    if (!this.myStartSent) return;

    if (this.oppStartReceived) {
      this.transitionToFight();
    } else if (!this.waitingText) {
      this.waitingText = this.add.text(GAME.WIDTH / 2, GAME.HEIGHT - 130,
        "⌛ Ожидаем подтверждение оппонента...", {
          fontFamily: "Arial Black", fontSize: "16px",
          color: "#ffaa00", stroke: "#000", strokeThickness: 3,
        }).setOrigin(0.5);
    }
  }

  private transitionToFight() {
    if (this.mpUnsub) { this.mpUnsub(); this.mpUnsub = undefined; }
    this.preview?.destroy();
    this.preview = undefined;
    this.scene.start("Fight", {
      p1Slug: this.p1Slug,
      p2Slug: this.p2Slug,
      online: this.online,
      mp: this.mp,
      myRole: this.myRole,
    });
  }
}
